import './styles.scss';
import { Button, Checkbox, Field, Input } from "@fluentui/react-components";
import { DataGrid, DataGridBody, DataGridCell, DataGridRow, TableColumnDefinition, createTableColumn } from "@fluentui/react-table";
import LoadingWrapper from "#root/components/LoadingWrapper";
import Paginator from "#root/components/Paginator";
import { AddRegular, CheckmarkRegular, DeleteRegular, DismissRegular, EditRegular, PersonDeleteRegular } from "@fluentui/react-icons";
import React, { useState } from "react";
import { showToast } from "#root/components/GlobalToaster";
import { DialogBox } from "#root/components/DialogBox";
import { Breadcrumbs } from "#root/components/Breadcrumbs";
import { ChiefComplaint, useBatchDeleteChiefComplaints, useChiefComplaints, useCreateChiefComplaint, useUpdateChiefComplaint } from "#root/queries/chiefComplaints";

const ChiefComplaints: React.FC = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [chiefComplaintsToDelete, setChiefComplaintsToDelete] = useState<ChiefComplaint[]>([]);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [selectedChiefComplaints, setSelectedChiefComplaints] = useState<Set<string>>(new Set());
    const [newChiefComplaint, setNewChiefComplaint] = useState({ name: '' });
    const [editingChiefComplaint, setEditingChiefComplaint] = useState<ChiefComplaint | null>(null);
    const [editedValues, setEditedValues] = useState<ChiefComplaint>({});

    const {
        data: chiefComplaintsResponse,
        isLoading,
        refetch
    } = useChiefComplaints({
        skip: (currentPage - 1) * itemsPerPage,
        take: itemsPerPage,
        includeDefault: false
    });

    const updateChiefComplaintMutation = useUpdateChiefComplaint();
    const batchDeleteChiefComplaintsMutation = useBatchDeleteChiefComplaints();
    const createChiefComplaintMutation = useCreateChiefComplaint();

    const totalCount = chiefComplaintsResponse?.totalCount ?? 0;
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const chiefComplaints = chiefComplaintsResponse?.items ?? [];

    // Create handling

    async function handleConfirmCreate() {
        try {
            await createChiefComplaintMutation.mutateAsync({
                chiefComplaint: newChiefComplaint
            });
            setIsCreateDialogOpen(false);
            setNewChiefComplaint({ name: '' });
            showToast(`Chief Complaint created successfully`, 'success');
        } catch (error) {
            showToast(`Failed to create chief complaint`, 'error');
        }
    };

    // Edit handling

    function handleEdit(chiefComplaint: ChiefComplaint) {
        setEditingChiefComplaint(chiefComplaint);
        setEditedValues(chiefComplaint);
    };

    async function handleEditConfirm() {
        if (editingChiefComplaint?.id && editedValues) {
            try {
                if (editedValues !== editingChiefComplaint) {
                    await updateChiefComplaintMutation.mutateAsync({
                        chiefComplaint: editedValues
                    });
                    showToast('Chief Complaint updated successfully', 'success');
                }
                setEditingChiefComplaint(null);
                setEditedValues({});
            } catch (error) {
                showToast('Failed to update chief complaint', 'error');
            }
        }
    };

    // Delete handling
    const handleDeleteClick = (chiefComplaint: ChiefComplaint, e?: React.MouseEvent) => {
        e?.stopPropagation();
        setChiefComplaintsToDelete([chiefComplaint]);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteMultipleClick = () => {
        const chiefComplaintsToDelete = chiefComplaints.filter(chiefComplaint => chiefComplaint.id && selectedChiefComplaints.has(chiefComplaint.id));
        setChiefComplaintsToDelete(chiefComplaintsToDelete);
        setIsDeleteDialogOpen(true);
    };

    async function handleConfirmDelete() {
        try {
            await batchDeleteChiefComplaintsMutation.mutateAsync({
                ids: chiefComplaintsToDelete.map(chiefComplaint => chiefComplaint.id!)
            });
            setIsDeleteDialogOpen(false);
            showToast(`${chiefComplaintsToDelete.length > 1 ? 'Chief Complaints' : 'Chief Complaint'} deleted successfully`, 'success');
            setSelectedChiefComplaints(new Set());
            setChiefComplaintsToDelete([]);
            refetch();
        } catch (error) {
            showToast('Failed to delete chief complaint(s)', 'error');
        }
    };

    // Pagination handling

    const handleItemsPerPageChange = (newItemsPerPage: number): void => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        refetch();
    };

    // Handle selection

    const onSelectionChange = (event: React.MouseEvent | React.KeyboardEvent, data: any) => {
        const target = event.target as HTMLElement;
        if (target.closest('[type="checkbox"]')) {
            setSelectedChiefComplaints(new Set(data.selectedItems));
        }
    };

    const getSelectionState = () => {
        if (selectedChiefComplaints.size === 0) return false;
        if (selectedChiefComplaints.size === chiefComplaints.length) return true;
        return 'mixed';
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedChiefComplaints(new Set(chiefComplaints.map(cc => cc.id!)));
        } else {
            setSelectedChiefComplaints(new Set());
        }
    };

    const columns: TableColumnDefinition<ChiefComplaint>[] = [
        createTableColumn<ChiefComplaint>({
            columnId: "name",
            renderHeaderCell: () => {
                return "Name";
            },
            renderCell: (item) => {
                return (
                    <>
                        {editingChiefComplaint?.id === item.id ? (
                            <Input
                                required
                                appearance="underline"
                                value={editedValues.name || ''}
                                onChange={(_, data) => setEditedValues(prev => ({ ...prev, name: data.value }))}
                                disabled={updateChiefComplaintMutation.isPending}
                            />
                        ) : (
                            <div title={item.name}>{item.name}</div>
                        )}
                    </>
                );
            },
        }),
        createTableColumn<ChiefComplaint>({
            columnId: "actions",
            renderHeaderCell: () => {
                return "Actions";
            },
            renderCell: (item) => {
                return (
                    editingChiefComplaint?.id === item.id ? (
                        <>
                            <Button
                                appearance="primary"
                                icon={<CheckmarkRegular />}
                                onClick={() => handleEditConfirm()}
                                disabled={updateChiefComplaintMutation.isPending}
                            />
                            <Button
                                appearance="secondary"
                                icon={<DismissRegular />}
                                onClick={() => setEditingChiefComplaint(null)}
                                disabled={updateChiefComplaintMutation.isPending}
                            />
                        </>
                    ) : (
                        <>
                            <Button
                                title="Edit Chief Complaint"
                                appearance="subtle"
                                icon={<EditRegular />}
                                onClick={() => handleEdit(item)}
                                disabled={updateChiefComplaintMutation.isPending}
                            />
                            <Button
                                title="Delete Chief Complaint"
                                appearance="subtle"
                                icon={<DeleteRegular />}
                                onClick={(e) => handleDeleteClick(item, e)}
                                disabled={batchDeleteChiefComplaintsMutation.isPending}
                            />
                        </>
                    )
                );
            },
        }),
    ];

    return (
        <div className="chief-complaints">
            <div className="emc-page-header">
                <div className="emc-page-header__top-row">
                    <div className="emc-page-header__breadcrumbs">
                        <Breadcrumbs />
                    </div>
                </div>
                <div className="emc-page-header__title">
                    <h2>Chief Complaints <div className="divider" />
                        <Button
                            appearance="primary"
                            icon={<AddRegular />}
                            onClick={() => setIsCreateDialogOpen(true)}>
                            New Chief Complaint
                        </Button>
                    </h2>
                </div>
            </div>

            <LoadingWrapper
                isLoading={isLoading}
                isEmpty={chiefComplaints.length === 0}
                emptyMessage="No chief complaints found."
                emptyIcon={<PersonDeleteRegular />}
            >
                <div className="emc-grid">
                    <DataGrid
                        className="emc-datagrid"
                        items={chiefComplaints}
                        columns={columns}
                        selectionMode="multiselect"
                        selectedItems={selectedChiefComplaints}
                        onSelectionChange={onSelectionChange}
                        getRowId={(item) => item.id!}
                        focusMode="none"
                    >
                        <DataGridBody<ChiefComplaint>>
                            {({ item, rowId }) => (
                                <DataGridRow<ChiefComplaint>
                                    key={rowId}
                                    selectionCell={{ "aria-label": "Select row" }}
                                    className={editingChiefComplaint?.id === item.id ? 'editing' : ''}
                                >
                                    {({ renderCell, columnId }) => (
                                        <DataGridCell className={columnId.toString()}>
                                            {renderCell(item)}
                                        </DataGridCell>
                                    )}
                                </DataGridRow>
                            )}
                        </DataGridBody>
                    </DataGrid>

                    <Paginator
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalCount}
                        currentItems={(currentPage - 1) * itemsPerPage + 1}
                        showItemsPerPage={true}
                    />
                </div>
            </LoadingWrapper>

            {selectedChiefComplaints.size > 0 && (
                <div className="emc-page-footer">
                    <Checkbox
                        checked={getSelectionState()}
                        onChange={(_, data) => handleSelectAll(!!data.checked)}
                        label="Select All"
                    />
                    <Button
                        appearance="secondary"
                        onClick={() => setSelectedChiefComplaints(new Set())}
                    >
                        Cancel
                    </Button>
                    <Button
                        appearance="primary"
                        icon={<DeleteRegular />}
                        onClick={handleDeleteMultipleClick}
                    >
                        Delete Selected
                    </Button>
                </div>
            )}

            <DialogBox
                open={isDeleteDialogOpen}
                onOpenChange={setIsDeleteDialogOpen}
                title={`Delete ${chiefComplaintsToDelete.length > 1 ? 'Chief Complaints' : 'Chief Complaint'}`}
                primaryButtonText="Delete"
                onPrimaryButtonClick={handleConfirmDelete}
                isPrimaryButtonLoading={batchDeleteChiefComplaintsMutation.isPending}
            >
                Permanently delete {chiefComplaintsToDelete.length === 1 ? `chief complaint ${chiefComplaintsToDelete[0]?.name}` : `${chiefComplaintsToDelete.length} chief complaints`}?
                You can't undo this action.
            </DialogBox>

            <DialogBox
                open={isCreateDialogOpen}
                onOpenChange={setIsCreateDialogOpen}
                title="New Chief Complaint"
                primaryButtonText="Save"
                onPrimaryButtonClick={handleConfirmCreate}
                isPrimaryButtonDisabled={!newChiefComplaint.name.trim()}
                isPrimaryButtonLoading={createChiefComplaintMutation.isPending}
            >
                <Field label="Chief Complaint" required>
                    <Input
                        required
                        value={newChiefComplaint.name}
                        placeholder="Please enter chief complaint name"
                        onChange={(e, data) => setNewChiefComplaint({ ...newChiefComplaint, name: data.value })}
                    />
                </Field>
            </DialogBox>
        </div>
    );
};

export default ChiefComplaints;